import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import {
  DeleteInvestorTypeResult,
  InvestorType,
  InvestorTypeApiItem,
  UpsertInvestorTypeRequest,
} from './investor-type.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class InvestorTypeService {
  private readonly endpoint = apiUrl('investortype');

  constructor(private readonly http: HttpClient) {}

  getInvestorTypes(): Observable<InvestorType[]> {
    return this.http.get<unknown>(this.endpoint).pipe(
      map((response) => this.mapRows(response, (item) => this.normalizeInvestorType(item))),
    );
  }

  createInvestorType(payload: UpsertInvestorTypeRequest): Observable<void> {
    return this.http.post<void>(this.endpoint, payload);
  }

  updateInvestorType(investorTypeId: number, payload: UpsertInvestorTypeRequest): Observable<void> {
    return this.http.put<void>(`${this.endpoint}/${investorTypeId}`, payload);
  }

  deleteInvestorType(investorTypeId: number): Observable<DeleteInvestorTypeResult> {
    return this.http.delete<void>(`${this.endpoint}/${investorTypeId}`).pipe(
      map((): DeleteInvestorTypeResult => ({ kind: 'success' })),
      catchError(() => of<DeleteInvestorTypeResult>({
        kind: 'error',
        message: 'Unable to delete investor type at this time. Please try again.',
      })),
    );
  }

  private normalizeInvestorType(item: InvestorTypeApiItem): InvestorType {
    return {
      id: this.toNumber(item.id),
      name: this.toText(item.name),
      description: this.toText(item.description),
    };
  }

  private mapRows<T>(response: unknown, mapper: (item: InvestorTypeApiItem) => T): T[] {
    return this.extractRows(response).map(mapper);
  }

  private extractRows(response: unknown): InvestorTypeApiItem[] {
    // API payloads can come as a bare array or wrapped under data/items.
    if (Array.isArray(response)) {
      return response as InvestorTypeApiItem[];
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = record['data'];
      const items = record['items'];

      if (Array.isArray(data)) {
        return data as InvestorTypeApiItem[];
      }

      if (Array.isArray(items)) {
        return items as InvestorTypeApiItem[];
      }
    }

    return [];
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }
}
